// ============================================================================
// Separation module — the data layer.
//
// ⛔ ONE CHECKLIST, TWO VIEWS. There is a single query for the items. Who can
// see which rows is decided by RLS on separation_checklist_items, not here:
// HR, Executive and Finance read every row for a separation; everybody else
// reads only the rows assigned to them or owned by a position they hold. The
// "My items" tab is a client-side FILTER over the same rows the consolidated
// tab shows — same wording, same sub-steps, same due date.
//
// So a person holding one item and a person holding the record fetch through
// exactly the same code path, and the one who should see less gets less
// because the database returned less. There is no client-side redaction to
// forget to write.
//
// ⛔ Employee pickers bind to v_employee_picker (Active only). v_am_name and
// v_employee_display are RESOLVERS and still return former staff on purpose.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import type {
  ChecklistItem,
  LooseEnd,
  PickerEmployee,
  SeparationRecord,
} from '../types';

const SEPARATION_COLUMNS =
  'separation_id, employee_id, separation_type, notice_date, last_working_day, reason, ' +
  'performance_trail_complete, eligible_for_rehire, supervisor_eid, final_pay_date, ' +
  'severance_offered, severance_length_days, severance_pay_through, severance_benefits_through, ' +
  'severance_payment_method, severance_release_required, severance_non_solicitation, ' +
  'severance_transition_assistance, severance_notes, ceo_attested, ceo_attested_on, ' +
  'ceo_attestation_note, approved_by_eid, payroll_total, payroll_total_published_at, ' +
  'document_folder_url, conversion_fee, access_revoked_at, status, completed_date, source, notes';

export interface SeparationListRow extends SeparationRecord {
  employee_name: string | null;
  open_items: number;
  blocking_items: number;
  total_items: number;
}

/** Everything the record page needs, in one loader. */
export function useSeparationRecord(separationId: string | undefined) {
  const [record, setRecord] = useState<SeparationRecord | null>(null);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!separationId) return;
    setLoading(true);
    setError(null);
    try {
      // The separation row can legitimately come back empty: somebody holding
      // one item may read that item and NOT the record. That is the design, so
      // an absent record is a state to render, never an error to throw.
      const [sepRes, itemRes] = await Promise.all([
        supabase
          .from('employee_separations')
          .select(SEPARATION_COLUMNS)
          .eq('separation_id', separationId)
          .maybeSingle(),
        supabase
          .from('separation_checklist_items')
          .select('*')
          .eq('separation_id', separationId)
          .order('sort_order'),
      ]);
      if (itemRes.error) throw itemRes.error;

      const rows = (itemRes.data ?? []) as unknown as ChecklistItem[];
      setRecord((sepRes.data as unknown as SeparationRecord) ?? null);
      setItems(rows);

      // Names resolve through v_am_name, which returns former staff too —
      // the leaver's own name has to render on their separation record.
      const eids = new Set<string>();
      if (sepRes.data) {
        const s = sepRes.data as unknown as SeparationRecord;
        [s.employee_id, s.supervisor_eid, s.approved_by_eid].forEach(
          (v) => v && eids.add(v),
        );
      }
      rows.forEach((r) => {
        [r.assigned_to_eid, r.assigned_by_eid, r.completed_by_eid].forEach(
          (v) => v && eids.add(v),
        );
        (r.sub_steps ?? []).forEach((s) => s.proposed_eid && eids.add(s.proposed_eid));
      });
      if (eids.size > 0) {
        const { data } = await supabase
          .from('v_am_name')
          .select('employee_id, display_name')
          .in('employee_id', Array.from(eids));
        const map = new Map<string, string>();
        (data ?? []).forEach((r: { employee_id: string; display_name: string | null }) => {
          if (r.display_name) map.set(r.employee_id, r.display_name);
        });
        setNames(map);
      }
    } catch (err) {
      setError(errorMessage(err, "Couldn't load this separation."));
    } finally {
      setLoading(false);
    }
  }, [separationId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { record, items, names, loading, error, refetch: load };
}

/** The list of separations. Consolidated readers only — RLS returns nothing otherwise. */
export function useSeparationList() {
  const [rows, setRows] = useState<SeparationListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('employee_separations')
        .select(SEPARATION_COLUMNS)
        .order('last_working_day', { ascending: false, nullsFirst: false });
      if (e) throw e;
      const seps = (data ?? []) as unknown as SeparationRecord[];

      const ids = seps.map((s) => s.separation_id);
      const counts = new Map<string, { open: number; blocking: number; total: number }>();
      if (ids.length > 0) {
        const { data: itemRows } = await supabase
          .from('separation_checklist_items')
          .select('separation_id, is_blocking, completed_at')
          .in('separation_id', ids);
        (itemRows ?? []).forEach(
          (r: { separation_id: string; is_blocking: boolean; completed_at: string | null }) => {
            const c = counts.get(r.separation_id) ?? { open: 0, blocking: 0, total: 0 };
            c.total += 1;
            if (!r.completed_at) {
              c.open += 1;
              if (r.is_blocking) c.blocking += 1;
            }
            counts.set(r.separation_id, c);
          },
        );
      }

      const eids = Array.from(new Set(seps.map((s) => s.employee_id)));
      const nameMap = new Map<string, string>();
      if (eids.length > 0) {
        const { data: nameRows } = await supabase
          .from('v_am_name')
          .select('employee_id, display_name')
          .in('employee_id', eids);
        (nameRows ?? []).forEach(
          (r: { employee_id: string; display_name: string | null }) => {
            if (r.display_name) nameMap.set(r.employee_id, r.display_name);
          },
        );
      }

      setRows(
        seps.map((s) => ({
          ...s,
          employee_name: nameMap.get(s.employee_id) ?? null,
          open_items: counts.get(s.separation_id)?.open ?? 0,
          blocking_items: counts.get(s.separation_id)?.blocking ?? 0,
          total_items: counts.get(s.separation_id)?.total ?? 0,
        })),
      );
    } catch (err) {
      setError(errorMessage(err, "Couldn't load separations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { rows, loading, error, refetch: load };
}

/**
 * Every open checklist item this signed-in person holds, across every
 * separation. RLS already scopes the rows, so this is the whole query.
 */
export function useMyOpenItems() {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('separation_checklist_items')
      .select('*')
      .is('completed_at', null)
      .eq('is_automatic', false)
      .order('sort_order');
    setItems((data ?? []) as unknown as ChecklistItem[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { items, loading, refetch: load };
}

/** ⛔ Pickers bind here. Active employees only — a leaver appears in none. */
export function useEmployeePicker() {
  const [people, setPeople] = useState<PickerEmployee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('v_employee_picker')
        .select('employee_id, display_name, employee_title, work_state')
        .order('display_name');
      if (!cancelled) {
        setPeople((data ?? []) as unknown as PickerEmployee[]);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { people, loading };
}

export function useLooseEnds() {
  const [rows, setRows] = useState<LooseEnd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('v_separation_loose_ends')
        .select('*')
        .order('separation_date', { ascending: false, nullsFirst: false });
      if (e) throw e;
      setRows((data ?? []) as unknown as LooseEnd[]);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load the Loose Ends report."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { rows, loading, error, refetch: load };
}

/**
 * Supervisor proposal. Active employees sharing the leaver's work_state first —
 * when an AM left Denver, the other Denver employee took the computer, vehicle,
 * keys and equipment, which is exactly what work_state finds.
 */
export async function fetchSupervisorCandidates(employeeId: string) {
  const { data, error } = await supabase.rpc('hr_supervisor_candidates', {
    p_employee_id: employeeId,
  });
  if (error) throw error;
  return (data ?? []) as Array<{
    employee_id: string;
    display_name: string | null;
    employee_title: string | null;
    work_state: string | null;
    proximity: number;
  }>;
}

// ---- Writes ---------------------------------------------------------------

export interface StartSeparationInput {
  employee_id: string;
  separation_type: string;
  notice_date: string;
  last_working_day: string;
  supervisor_eid?: string | null;
  reason?: string | null;
  performance_trail_complete?: string | null;
  ceo_attested?: boolean;
  ceo_attested_on?: string | null;
  ceo_attestation_note?: string | null;
  eligible_for_rehire?: string | null;
  final_pay_date?: string | null;
  severance_offered?: boolean;
  severance_length_days?: number | null;
  severance_pay_through?: string | null;
  severance_benefits_through?: string | null;
  severance_payment_method?: string | null;
  severance_release_required?: boolean;
  severance_non_solicitation?: boolean;
  severance_transition?: boolean;
  severance_notes?: string | null;
  document_folder_url?: string | null;
  notes?: string | null;
}

export async function startSeparation(input: StartSeparationInput) {
  const { data, error } = await supabase.rpc('hr_start_separation', {
    p_employee_id: input.employee_id,
    p_separation_type: input.separation_type,
    p_notice_date: input.notice_date,
    p_last_working_day: input.last_working_day,
    p_supervisor_eid: input.supervisor_eid ?? null,
    p_reason: input.reason ?? null,
    p_performance_trail_complete: input.performance_trail_complete ?? null,
    p_ceo_attested: input.ceo_attested ?? false,
    p_ceo_attested_on: input.ceo_attested_on ?? null,
    p_ceo_attestation_note: input.ceo_attestation_note ?? null,
    p_eligible_for_rehire: input.eligible_for_rehire ?? null,
    p_final_pay_date: input.final_pay_date ?? null,
    p_severance_offered: input.severance_offered ?? false,
    p_severance_length_days: input.severance_length_days ?? null,
    p_severance_pay_through: input.severance_pay_through ?? null,
    p_severance_benefits_through: input.severance_benefits_through ?? null,
    p_severance_payment_method: input.severance_payment_method ?? null,
    p_severance_release_required: input.severance_release_required ?? false,
    p_severance_non_solicitation: input.severance_non_solicitation ?? false,
    p_severance_transition: input.severance_transition ?? false,
    p_severance_notes: input.severance_notes ?? null,
    p_document_folder_url: input.document_folder_url ?? null,
    p_notes: input.notes ?? null,
    p_actor_eid: null,
  });
  if (error) throw error;
  return data as Record<string, unknown>;
}

export async function assignItem(
  itemId: string,
  toEid: string,
  note: string | null,
  keepFollowing: boolean,
) {
  const { error } = await supabase.rpc('separation_item_assign', {
    p_item_id: itemId,
    p_to_eid: toEid,
    p_note: note,
    p_keep_following: keepFollowing,
  });
  if (error) throw error;
}

export async function completeItem(
  itemId: string,
  note: string | null,
  odometer?: number | null,
) {
  const { error } = await supabase.rpc('separation_item_complete', {
    p_item_id: itemId,
    p_note: note,
    p_odometer: odometer ?? null,
  });
  if (error) throw error;
}

export async function setSubSteps(itemId: string, subSteps: unknown) {
  const { error } = await supabase.rpc('separation_item_set_sub_steps', {
    p_item_id: itemId,
    p_sub_steps: subSteps,
  });
  if (error) throw error;
}

/** "Revoke now" — the COO's early override on the automatic access item. */
export async function revokeAccessNow(separationId: string) {
  const { error } = await supabase.rpc('hr_release_employee_access', {
    p_separation_id: separationId,
  });
  if (error) throw error;
}

export async function publishPayrollTotal(
  separationId: string,
  total: number,
  note: string | null,
) {
  const { error } = await supabase.rpc('hr_publish_payroll_total', {
    p_separation_id: separationId,
    p_total: total,
    p_note: note,
  });
  if (error) throw error;
}

export async function completeSeparation(separationId: string, note: string | null) {
  const { error } = await supabase.rpc('hr_complete_separation', {
    p_separation_id: separationId,
    p_note: note,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Step 2 of the wizard — "what they hold", before anything is written.
//
// The generator runs the same questions server-side when the separation is
// recorded; this is the preview, so the person starting it sees the true scale
// of the handoff before they commit to a date. ⛔ employee_cc_assignment.active
// is the TEXT 'Y'. A boolean filter returns nothing and reads as "no card".
// ---------------------------------------------------------------------------
export interface Holdings {
  facilities: number;
  route_stops: number;
  open_tasks: number;
  pipeline_cards: number;
  routings: number;
  notifications: number;
  follows: number;
  vehicles: number;
  cards: number;
  card_last4: string | null;
  app_profile: number;
  partner_onboardings: number;
}

export async function fetchHoldings(employeeId: string): Promise<Holdings> {
  const head = { count: 'exact' as const, head: true };
  const [
    facilities,
    stops,
    tasks,
    cards,
    routings,
    notifs,
    follows,
    vehicles,
    ccRows,
    profiles,
    onbs,
    empOnbs,
    hireReqs,
  ] = await Promise.all([
    supabase.from('facilities').select('facility_id', head).eq('account_manager_eid', employeeId).neq('status', 'Closed'),
    supabase.from('am_route_assignments').select('assignment_id', head).eq('am_eid', employeeId).eq('active', true),
    supabase.from('facility_tasks').select('task_id', head).eq('assigned_to', employeeId).eq('status', 'Open'),
    supabase.from('new_account_sop').select('sop_id', head).or(`bdd_eid.eq.${employeeId},am_eid.eq.${employeeId}`).not('status', 'in', '("Completed","Cancelled")'),
    supabase.from('record_routing').select('routing_id', head).eq('routed_to_eid', employeeId).eq('status', 'Open'),
    supabase.from('notification_recipients').select('recipient_row_id', head).eq('recipient_eid', employeeId).eq('status', 'active'),
    supabase.from('subscriptions').select('subscription_id', head).eq('subscriber_eid', employeeId),
    supabase.from('vehicles').select('vehicle_id', head).eq('assigned_employee_eid', employeeId),
    supabase.from('employee_cc_assignment').select('cc_last4').eq('employee_id', employeeId).eq('active', 'Y'),
    supabase.from('user_profiles').select('id', head).eq('employee_id', employeeId),
    // TCSD-0162 — this asked `.eq('hr_owner', employeeId)`, and hr_owner held an
    // employee id on partner_onboarding but a display NAME on the other two, so it
    // matched 89 rows and silently missed 40. It now reads assigned_to_eid, which is
    // an id everywhere, on all three tables a departing person can still be holding.
    supabase.from('partner_onboarding').select('onboarding_id', head).eq('assigned_to_eid', employeeId).not('status', 'in', '("Completed","Cancelled")'),
    supabase.from('employee_onboarding').select('onboarding_id', head).eq('assigned_to_eid', employeeId).not('status', 'in', '("Completed","Cancelled")'),
    supabase.from('hiring_requests').select('request_id', head).eq('assigned_to_eid', employeeId).not('status', 'in', '("Filled","Cancelled","Closed")'),
  ]);

  const cc = (ccRows.data ?? []) as Array<{ cc_last4: string }>;
  return {
    facilities: facilities.count ?? 0,
    route_stops: stops.count ?? 0,
    open_tasks: tasks.count ?? 0,
    pipeline_cards: cards.count ?? 0,
    routings: routings.count ?? 0,
    notifications: notifs.count ?? 0,
    follows: follows.count ?? 0,
    vehicles: vehicles.count ?? 0,
    cards: cc.length,
    card_last4: cc[0]?.cc_last4 ?? null,
    app_profile: profiles.count ?? 0,
    partner_onboardings: (onbs.count ?? 0) + (empOnbs.count ?? 0) + (hireReqs.count ?? 0),
  };
}

/**
 * Correct the two dates on a separation.
 *
 * ⛔ A DATE NOBODY WROTE DOWN IS A CORRECTION, NOT A NEW EVENT. This does not
 * re-run the release cascade and does not reopen a completed separation — the
 * three backfilled temporary employees are `Completed` and still need their
 * date set. `employees.separation_date` follows the record automatically.
 */
export async function updateSeparationDates(
  separationId: string,
  lastWorkingDay: string | null,
  noticeDate: string | null,
  note: string | null,
) {
  const { error } = await supabase.rpc('hr_update_separation_dates', {
    p_separation_id: separationId,
    p_last_working_day: lastWorkingDay,
    p_notice_date: noticeDate,
    p_note: note,
  });
  if (error) throw error;
}
